import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsuarioSistemaId, getAuthToken } from '../utils/auth';
import './Home.css';
import BASE_URL from '../utils/api';

export default function MinhasConexoes() {
  const [conexoes, setConexoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removendo, setRemovendo] = useState(null); // ID da conexão sendo removida
  const navigate = useNavigate();

  // Função para chamadas de API
  const apiCall = useCallback(async (endpoint, method = 'GET', body = null) => {
    const urls = [
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`
    ];

    for (const url of urls) {
      try {
        const options = {
          method,
          headers: { 'Content-Type': 'application/json' }
        };
        const token = getAuthToken();
        if (token) options.headers['Authorization'] = 'Bearer ' + token;
        if (body) options.body = JSON.stringify(body);
        
        const response = await fetch(url, options);
        if (response.ok) {
          if (method === 'DELETE') return { success: true };
          return await response.json();
        }
      } catch (error) {
        console.log(`Erro em: ${url}`, error.message);
      }
    }
    throw new Error('Nenhuma URL da API funcionou');
  }, []);

  // Carregar conexões
  const carregarConexoes = useCallback(async () => {
    setLoading(true);
    try {
      const meuId = getUsuarioSistemaId();
      if (!meuId) {
        setConexoes([]);
        return;
      }

      // Buscar conexões onde eu sou a origem
      const todasConexoes = await apiCall('ConexaoUsuario', 'GET');
      const arrConexoes = Array.isArray(todasConexoes) ? todasConexoes : [];
      const minhasConexoesIds = arrConexoes
        .filter(c => (c.UsuarioIdOrigem || c.usuarioIdOrigem) === meuId)
        .map(c => c.UsuarioIdDestino || c.usuarioIdDestino);

      if (minhasConexoesIds.length === 0) {
        setConexoes([]);
        return;
      }

      // Buscar dados dos usuários conectados via DadosUsuario
      const usuariosConectados = [];
      for (const usuarioSistemaId of minhasConexoesIds) {
        try {
          // Buscar dados do usuário (login)
          const usuarioSistema = await apiCall(`UsuarioSistema/${usuarioSistemaId}`, 'GET');
          const login = usuarioSistema?.Login || usuarioSistema?.login || '';
          
          // Buscar nome via DadosUsuario
          let nomeCompleto = login;
          try {
            const dadosUsuario = await apiCall(`DadosUsuario/por-usuario/${usuarioSistemaId}`, 'GET');
            nomeCompleto = dadosUsuario?.nomeUsuario || dadosUsuario?.NomeUsuario || dadosUsuario?.Nome || dadosUsuario?.nome || login;
          } catch (e) { /* mantém login como nome */ }
          
          usuariosConectados.push({
            id: usuarioSistemaId,
            nome: nomeCompleto,
            login: login
          });
        } catch (e) {
          console.error('Erro ao buscar dados do usuário:', e);
        }
      }

      setConexoes(usuariosConectados);
    } catch (err) {
      console.error('Erro ao carregar conexões:', err);
      setConexoes([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    carregarConexoes();
  }, [carregarConexoes]);

  // Desfazer conexão
  const handleDesfazerConexao = async (usuarioDestinoId) => {
    const meuId = getUsuarioSistemaId();
    if (!meuId) return;

    setRemovendo(usuarioDestinoId);
    try {
      // DELETE na conexão onde origem = meu ID e destino = usuário clicado
      await apiCall(`ConexaoUsuario/${meuId}/${usuarioDestinoId}`, 'DELETE');
      
      // Remover da lista local
      setConexoes(prev => prev.filter(u => (u.Id || u.id) !== usuarioDestinoId));
      
      // Disparar evento para atualizar sidebar
      window.dispatchEvent(new CustomEvent('conexoesChanged'));
    } catch (err) {
      console.error('Erro ao desfazer conexão:', err);
      alert('Erro ao desfazer conexão. Tente novamente.');
    } finally {
      setRemovendo(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Título */}
        <h1 style={{
          textAlign: 'center',
          color: 'rgb(235, 133, 0)',
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '40px'
        }}>
          Minhas Conexões
        </h1>

        {/* Loading */}
        {loading && (
          <div style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '16px',
            padding: '40px'
          }}>
            Carregando conexões...
          </div>
        )}

        {/* Lista vazia */}
        {!loading && conexoes.length === 0 && (
          <div style={{
            textAlign: 'center',
            color: '#888',
            fontSize: '16px',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤝</div>
            Você ainda não tem conexões.
            <br />
            <span style={{ fontSize: '14px', color: '#aaa' }}>
              Conecte-se com outros usuários através do perfil deles.
            </span>
          </div>
        )}

        {/* Lista de conexões */}
        {!loading && conexoes.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {conexoes.map((u, i) => {
              const nome = u.nome || '';
              const login = u.login || '';
              const uid = u.id;
              // Pegar primeira letra do primeiro nome
              const primeiroNome = nome.split(' ')[0] || '';
              const inicial = primeiroNome.charAt(0).toUpperCase() || '?';
              const estaRemovendo = removendo === uid;

                  return (
                <div
                  key={uid || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'white',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s'
                  }}
                  onClick={() => navigate('/sidebar/Funcionario', { state: { funcionario: { id: uid, Id: uid, login: login, Login: login, nome: nome } } })}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Bolinha laranja com inicial */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: 'rgb(235, 133, 0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '20px',
                      flexShrink: 0
                    }}>
                      {inicial}
                    </div>
                    
                    {/* Nome e login */}
                    <div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#333',
                        marginBottom: '4px'
                      }}>
                        {nome}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#888'
                      }}>
                        {login}
                      </div>
                    </div>
                  </div>

                  {/* Botão Desfazer Conexão */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDesfazerConexao(uid);
                    }}
                    disabled={estaRemovendo}
                    style={{
                      backgroundColor: estaRemovendo ? '#ccc' : 'rgb(235, 133, 0)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: estaRemovendo ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {estaRemovendo ? 'Removendo...' : 'Desfazer conexão'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Botão voltar */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '12px 30px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            ← Voltar
          </button>
        </div>
      </div>
    </div>
  );
}
